import React, { useContext, useEffect, useMemo } from 'react';
import { PageModel } from '@adobe/aem-react-editable-components';
import { useLocation } from 'react-router-dom';
import { useQuantumDispatch, useQuantumSelector } from '@quantum/tools/redux';
import { attemptToReload, clientSideRunner } from '../../utils/common';
import { setupButtonTags, getAssemblerPageViewTagData } from '../../utils/analytics';
import { setTilesToWaitFor, forceReady, reset, AnalyticReducerState } from '../../../../assembler-server/redux/analyticsSlice';
import { deepMerge } from '../../utils/deepMerge';
import { AEMPageProps } from '../../../../modules/remote-ucl/types/UCL';
import { RemoteUclContext } from '../../../../modules/remote-ucl/context';

export interface PageData {
  client: string;
  hierarchy: string;
  pageName: string;
  pageNameNoVehicle: string;
  radUIVersion: string;
  site: string;
  siteSection: string;
  userLanguage: string;
  previousPage: string;
  componentList: string[];
}

export interface CustomPageModel extends PageModel {
  page?: PageData;
  adobeVariant?: string;
  analyticsDisabled?: boolean;
  seoTitle?: string;
  title?: string;
  waitForTiles?: string[];
  waitForMFE?: string[];
}

export interface PageProps {
  model: CustomPageModel;
  pagePath: string;
  path: string;
  aemHost?: string;
  brand?: string;
  children: React.ReactElement<AEMPageProps & { brand: string }>;
  deviceType: 'mobile' | 'tablet' | 'desktop';
}

export function Page(props: Readonly<PageProps>) {
  const dispatch = useQuantumDispatch();
  const { pathname } = useLocation();
  const { AnalyticsContext } = useContext(RemoteUclContext);
  const { onAnalyticsEvent } = useContext(AnalyticsContext);

  const model: CustomPageModel = useMemo(() => {
    if (props.pagePath === props.model[':path']) {
      return props.model;
    }
    if (props.model[':children']?.[props.pagePath]) {
      return props.model[':children']?.[props.pagePath];
    }

    return null;
  }, [props.pagePath, props.model]) as CustomPageModel;

  useMemo(() => {
    clientSideRunner(() => {
      if (model) {
        dispatch(setTilesToWaitFor({ path: props.path, tiles: model.waitForTiles ?? [] }));
        if (model.waitForTiles && model.waitForTiles.length > 0) {
          setTimeout(() => {
            dispatch(forceReady({ path: props.path }));
          }, 7500);
        }

        document.title = (model.seoTitle || model.title) as string;
      }

      if (!model) {
        attemptToReload(pathname);
      }
    });
  }, []);

  const tileStatus = useQuantumSelector((state) => {
    const analyticModel = (state as unknown as { analytics: AnalyticReducerState }).analytics[props.path];
    if (analyticModel?.ready) {
      return analyticModel.tileStatus;
    }
    return null;
  });
  const mfePageData = useMemo(() => {
    if (tileStatus && typeof tileStatus === 'object') {
      return deepMerge(...Object.values(tileStatus as Record<string, object>));
    }
    return undefined;
  }, [tileStatus]);

  useEffect(() => {
    if (!model || model.analyticsDisabled) {
      return;
    }

    if (model.page && mfePageData) {
      const { page: mfePageViewTagData = {}, ...mfeOtherTagData } = mfePageData as Record<string, object>;
      const digitalData = { page: { ...model.page, ...mfePageViewTagData, ...getAssemblerPageViewTagData() }, ...mfeOtherTagData };
      onAnalyticsEvent('global-load', digitalData);
    }
  }, [mfePageData, model.page, model.analyticsDisabled, onAnalyticsEvent]);

  useEffect(() => {
    globalThis.appReady = true;

    return () => {
      dispatch(reset(props.path));
    };
  }, [dispatch, props.path]);

  useEffect(() => {
    if (!model.analyticsDisabled && model.page) {
      globalThis.addEventListener('click', setupButtonTags);
      return () => globalThis.removeEventListener('click', setupButtonTags);
    }
  }, [model.analyticsDisabled, model.page]);
  return (
    <>
      {React.cloneElement(props.children, {
        pagePath: props.pagePath,
        aemHost: props.aemHost,
        brand: props.brand,
        model,
        _defaultViewport: props.deviceType
      })}
    </>
  );
}
